import { useMemo } from "react";
import { Box, SxProps, Tooltip } from "@mui/material";
import {
  UsersIcon,
  BookOpenIcon,
  WrenchScrewdriverIcon,
  BugAntIcon,
  HomeIcon,
  RectangleGroupIcon,
  PresentationChartLineIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "features/auth";
import { useBackendVersion } from "@hooks/useBackendVersion";
import { frontendBuildInfo } from "services/version/version-service";
import { UserRoleEnum } from "features/users/types";
import { SidebarMenuItem } from "./SidebarMenuItem";
import { sidebarStyle, sidebarInnerStyle, HangfireItem, buildInfoStyle } from "./styles";

export interface MenuItemProps {
  to: string;
  label: string;
  Icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  minAccessLevel?: UserRoleEnum;
  target?: "_blank";
  styles?: SxProps;
}

const MENU_ITEMS: MenuItemProps[] = [
  { to: "/workspaces/1", label: "Workspaces", Icon: HomeIcon },
  { to: "/statistics", label: "Statistics", Icon: PresentationChartLineIcon, minAccessLevel: UserRoleEnum.Manager },
  { to: "/users", label: "Users", Icon: UsersIcon, minAccessLevel: UserRoleEnum.Manager },
  { to: "/languages", label: "Languages", Icon: BookOpenIcon, minAccessLevel: UserRoleEnum.Admin },
  { to: "/validation-rules", label: "Validation Rules", Icon: RectangleGroupIcon, minAccessLevel: UserRoleEnum.Admin },
  { to: "/data-sync", label: "Sync utils", Icon: WrenchScrewdriverIcon, minAccessLevel: UserRoleEnum.Admin },
  {
    to: `${window.appConfig.baseUrl}/hangfire`,
    label: "Hangfire",
    Icon: BugAntIcon,
    minAccessLevel: UserRoleEnum.Admin, // TODO: move to owner on release
    target: "_blank",
    styles: HangfireItem,
  },
];

export const Sidebar = () => {
  const { userRole } = useAuth();
  const backendVersion = useBackendVersion();
  const items = useMemo(
    () => MENU_ITEMS.filter(item => (item.minAccessLevel ? userRole <= item.minAccessLevel : true)),
    [userRole],
  );

  return (
    <Box sx={sidebarStyle}>
      <Box sx={sidebarInnerStyle}>
        {items.map(item => (
          <SidebarMenuItem
            to={item.to}
            label={item.label}
            Icon={item.Icon}
            key={item.to}
            target={item.target}
            styles={item.styles}
          />
        ))}
      </Box>
      <Box sx={buildInfoStyle}>
        <Tooltip title={`Built ${frontendBuildInfo.buildTime}`} placement="right">
          <Box>front: {`${frontendBuildInfo.version} · ${frontendBuildInfo.commit}`}</Box>
        </Tooltip>
        <Tooltip
          title={backendVersion ? `Built ${backendVersion.buildTime}, ${backendVersion.environment}` : ""}
          placement="right"
        >
          <Box>back: {backendVersion ? `${backendVersion.version} · ${backendVersion.commit}` : "—"}</Box>
        </Tooltip>
      </Box>
    </Box>
  );
};
